// Component for pair selection (first + second) with full React Hook Form integration and badge support

"use client";

import { Badge } from "@/components/ui/badge";
import { badgeColors } from "@/lib";
import { cn } from "@/lib/utils";
import { useController, useFormContext } from "react-hook-form";
import { FaXmark } from "react-icons/fa6";
import { useEffect, useMemo, useCallback, useState } from "react";
import { SelectInput } from "./select-input";
import { SelectSearchInput } from "./select-search-input";
import { Button } from "@/components";
import { MobileCollapsibleBadges } from "./MobileCollapsibleBadges";
import { useTranslations } from "next-intl";

export type Item = {
  id: string;
  name: string;
  category_id?: string | number;
};

export type ItemSecond = {
  id: string;
  name: string;
};

export type SingleSelectPair = {
  first_id: string | number;
  name_first: string;
  second_id: string | number;
  name_second: string;
  category_id?: string | number;
};

export type SinglePairSelectorFieldProps = {
  name: string; // field that gets saved with react-hook-form
  firstOptions: Item[];
  secondOptions: ItemSecond[];
  label?: string;
  placeholderFirst?: string;
  placeholderSecond?: string;
  placeholderSelectCategoryFirst?: string;
  onFirstSearch?: (query: string) => void;
  hasMoreFirst?: boolean;
  onLoadMoreFirst?: () => void;
  loadingFirst?: boolean;
  className?: string;
  showFilter?: boolean;
  required?: boolean;
  syncCategory?: boolean; // true to sync with categories
  categoryFieldName?: string; // name of the categories field
  onCategoryChange?: (categoryId: string | number) => void; // callback when category changes
  categoryOptions?: { id: string; name: string }[]; // available categories to sync with
  manualAdd?: boolean; // if true, don't auto-add on select; show Add button instead and enable edit-on-click
  secondWidthClass?: string; // width class for the second (years) dropdown wrapper
  badgesMaxHeightClass?: string; // optional max-height class to limit pills stack height
  allowNoSecond?: boolean; // if true, user can add entry without selecting second value
  noBorder?: boolean; // if true, remove border from inputs
  noShadow?: boolean; // if true, remove shadow from inputs
  bgStyleFirst?: string;
  bgStyleSecond?: string;
  addButtonText?: string; // text for Add button (default: "Add")
  updateButtonText?: string; // text for Update button (default: "Update")
};

export function SinglePairSelectorField({
  name,
  firstOptions,
  secondOptions,
  label,
  placeholderFirst,
  placeholderSecond,
  placeholderSelectCategoryFirst,
  onFirstSearch,
  hasMoreFirst = false,
  onLoadMoreFirst,
  loadingFirst = false,
  className = "",
  showFilter = true,
  required = false,
  syncCategory = false,
  categoryFieldName = "categories",
  onCategoryChange,
  categoryOptions = [],
  manualAdd = false,
  secondWidthClass,
  badgesMaxHeightClass,
  allowNoSecond = false,
  noBorder = false,
  noShadow = false,
  bgStyleFirst,
  bgStyleSecond,
  addButtonText,
  updateButtonText,
}: SinglePairSelectorFieldProps) {
  const t = useTranslations('selectComponents');
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  // Controller for the main array of values
  const {
    field: { value: selectedPairs, onChange: setSelectedPairs },
  } = useController({
    name,
    control,
    defaultValue: [],
  }) as {
    field: {
      value: SingleSelectPair[];
      onChange: (value: SingleSelectPair[]) => void;
    };
  };

  // Controller for the first temporary select
  const {
    field: { value: tempFirst, onChange: setTempFirst },
  } = useController({
    name: `__temp_first_${name}`,
    control,
    defaultValue: null,
  }) as {
    field: {
      value: Item | null;
      onChange: (value: Item | null) => void;
    };
  };

  // Controller for the second temporary select
  const {
    field: { value: tempSecond, onChange: setTempSecond },
  } = useController({
    name: `__temp_second_${name}`,
    control,
    defaultValue: null,
  }) as {
    field: {
      value: ItemSecond | null;
      onChange: (value: ItemSecond | null) => void;
    };
  };

  const watchedCategories = watch(categoryFieldName);

  const memoizedSelectedCategories = useMemo(() => {
    return watchedCategories || [];
  }, [watchedCategories]);

  const filteredFirstOptions = useMemo(() => {
    const selectedIds = new Set(
      (selectedPairs || []).map((pair) => String(pair.first_id))
    );

    let base = firstOptions.filter(
      (option) => !selectedIds.has(String(option.id))
    );

    if (tempFirst && !base.some((opt) => String(opt.id) === String(tempFirst.id))) {
      base = [tempFirst, ...base];
    }

    return base;
  }, [firstOptions, selectedPairs, tempFirst]);

  // Check if first select should be disabled - memoized
  const isFirstSelectDisabled = useMemo(
    () => syncCategory && memoizedSelectedCategories.length === 0,
    [syncCategory, memoizedSelectedCategories]
  );

  // Function to add category automatically - memoized with useCallback
  const addCategoryIfNeeded = useCallback(
    (item: Item) => {
      if (!syncCategory || !item.category_id) return;

      const categoryExists = memoizedSelectedCategories.some(
        (cat: { id: string; name: string }) => cat.id === item.category_id
      );

      if (!categoryExists) {
        // Find category in available category options
        const categoryOption = categoryOptions.find(
          (opt) => opt.id === item.category_id
        );
        if (categoryOption) {
          const newCategories = [...memoizedSelectedCategories, categoryOption];
          setValue(categoryFieldName, newCategories);
          onCategoryChange?.(item.category_id);
        }
      }
    },
    [
      syncCategory,
      memoizedSelectedCategories,
      categoryOptions,
      setValue,
      categoryFieldName,
      onCategoryChange,
    ]
  );

  // Local editing state for manual add mode
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Effect to handle when both elements are selected (auto mode only)
  useEffect(() => {
    if (manualAdd) return; // skip auto-add when manual mode enabled
  if (tempFirst && (tempSecond || allowNoSecond)) {
      const newEntry: SingleSelectPair = {
        first_id: tempFirst.id,
        name_first: tempFirst.name,
    second_id: tempSecond ? tempSecond.id : "",
    name_second: tempSecond ? tempSecond.name : "",
        category_id: tempFirst.category_id,
      };

      // Check if this combination already exists
      const exists = selectedPairs.some(
        (pair) =>
          pair.first_id === newEntry.first_id &&
          pair.second_id === newEntry.second_id
      );

      if (!exists) {
        setSelectedPairs([...selectedPairs, newEntry]);

        // Add category if needed
        addCategoryIfNeeded(tempFirst);
      }

      // Reset both temporary selects
      setTempFirst(null);
      setTempSecond(null);
    }
  }, [
    manualAdd,
    tempFirst,
    tempSecond,
    allowNoSecond,
    selectedPairs,
    setSelectedPairs,
    setTempFirst,
    setTempSecond,
    addCategoryIfNeeded,
  ]);

  // Effect to reset second select when first changes (skip during edit)
  useEffect(() => {
  // In manualAdd mode we should NOT aggressively clear the years (second) selection; user picks first then years then clicks Add.
  // Previous logic cleared tempSecond immediately after it was picked (because tempFirst still selected),
  // causing the dropdown to close with nothing persisted.
  if (manualAdd) return;
  if (tempFirst && tempSecond) setTempSecond(null);
  }, [tempFirst, tempSecond, setTempSecond, manualAdd, editingIndex]);

  const removeBadge = (
    index: number,
    e?: React.MouseEvent
  ) => {
    e?.preventDefault();
    e?.stopPropagation();

    const pairToRemove = selectedPairs[index];

    const updatedPairs = selectedPairs.filter((_, i) => i !== index);
    setSelectedPairs(updatedPairs);

    // If category sync is enabled, check if category can be removed
    if (syncCategory && pairToRemove?.category_id) {
      const categoryStillInUse = updatedPairs.some(
        (pair) => pair.category_id === pairToRemove.category_id
      );

      if (!categoryStillInUse) {
        // Remove category if no longer used
        const updatedCategories = memoizedSelectedCategories.filter(
          (cat: { id: string; name: string }) =>
            cat.id !== pairToRemove.category_id
        );
        setValue(categoryFieldName, updatedCategories);
      }
    }

    // If we deleted the item being edited, reset edit state
    if (editingIndex !== null && index === editingIndex) {
      setEditingIndex(null);
      setTempFirst(null);
      setTempSecond(null);
    }
  };

  const handleBadgeEditClick = (pair: SingleSelectPair, index: number) => {
    if (!manualAdd) return; // editing only in manual mode
    setEditingIndex(index);
    // Find the actual option objects to avoid id type mismatches
    const firstItem = firstOptions.find((i) => String(i.id) === String(pair.first_id));
    const secondItem = secondOptions.find((i) => String(i.id) === String(pair.second_id));
    setTempFirst(firstItem || { id: pair.first_id as any, name: pair.name_first, category_id: pair.category_id });
    setTempSecond(secondItem || { id: pair.second_id as any, name: pair.name_second });
  };

  const handleAddOrUpdate = () => {
  if (!tempFirst || (!tempSecond && !allowNoSecond)) return;

    const newEntry: SingleSelectPair = {
      first_id: tempFirst.id,
      name_first: tempFirst.name,
  second_id: tempSecond ? tempSecond.id : "",
  name_second: tempSecond ? tempSecond.name : "",
      category_id: tempFirst.category_id,
    };

    // Check duplicates (ignore the same index when editing)
    const duplicateIndex = selectedPairs.findIndex(
      (p, i) =>
        i !== (editingIndex ?? -1) &&
        String(p.first_id) === String(newEntry.first_id) &&
        String(p.second_id) === String(newEntry.second_id)
    );
    if (duplicateIndex !== -1) {
      // If duplicate exists, simply reset editor and do nothing
      setEditingIndex(null);
      setTempFirst(null);
      setTempSecond(null);
      return;
    }

    if (editingIndex !== null) {
      const prev = selectedPairs[editingIndex];
      const updated = [...selectedPairs];
      updated[editingIndex] = newEntry;
      setSelectedPairs(updated);

      // Category sync adjustments
      addCategoryIfNeeded(tempFirst);
      if (syncCategory && prev?.category_id && prev.category_id !== newEntry.category_id) {
        const stillInUse = updated.some((p) => p.category_id === prev.category_id);
        if (!stillInUse) {
          const updatedCategories = memoizedSelectedCategories.filter(
            (cat: { id: string; name: string }) => cat.id !== prev.category_id
          );
          setValue(categoryFieldName, updatedCategories);
        }
      }
    } else {
      setSelectedPairs([...selectedPairs, newEntry]);
      addCategoryIfNeeded(tempFirst);
    }

    // Reset temp selections and editing state
    setEditingIndex(null);
    setTempFirst(null);
    setTempSecond(null);
  };

  return (
    <div className={cn("flex flex-col", className)}>
      {label && (
        <label className="block mb-2 text-sm font-semibold text-gray-600 whitespace-nowrap">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="flex flex-col md:flex-row gap-2 w-full items-stretch">
        <div className="flex-1 min-w-0">
          <SelectSearchInput
            name={`__temp_first_${name}`}
            items={filteredFirstOptions}
            placeholder={
              isFirstSelectDisabled
                ? (placeholderSelectCategoryFirst || t('selectCategoryFirst'))
                : (placeholderFirst || t('selectFirst'))
            }
            onSearch={onFirstSearch}
            hasMore={hasMoreFirst}
            onLoadMore={onLoadMoreFirst}
            loading={loadingFirst}
            onResetPage={() => {}}
            className={cn(
              "w-full",
              bgStyleFirst,
              isFirstSelectDisabled && "opacity-50 pointer-events-none"
            )}
            showFilter={showFilter}
            noBorder={noBorder}
            noShadow={noShadow}
          />
        </div>

  <div className={cn(secondWidthClass ?? "md:w-[200px]", "w-full") }>
          <SelectInput
            name={`__temp_second_${name}`}
            options={secondOptions}
            placeholder={placeholderSecond || t('selectSecond')}
            className={cn(
              "w-full transition-opacity",
              bgStyleSecond,
              tempFirst ? "opacity-100" : "opacity-50 pointer-events-none"
            )}
            noBorder={noBorder}
            noShadow={noShadow}
          />
        </div>

        {manualAdd && (
          <div className="md:w-[120px] w-full flex md:items-end">
            <Button
              type="button"
              className="w-full md:w-[120px] h-[38px] md:h-[40px]"
              rounded="rounded-md"
              disabled={!tempFirst || (!tempSecond && !allowNoSecond)}
              onClick={handleAddOrUpdate}
            >
              {editingIndex !== null ? (updateButtonText || t('update')) : (addButtonText || t('add'))}
            </Button>
          </div>
        )}
      </div>

      {/* Badges section */}
      {/* Mobile Collapsible Badges */}
      <MobileCollapsibleBadges
        items={selectedPairs.map(pair => ({ 
          id: `${pair.first_id}-${pair.second_id}`,
          name: pair.name_first,
          secondValue: pair.name_second
        }))}
        sectionTitle={label || "Selected Items"}
        onItemClick={manualAdd ? (itemId) => {
          const pairIndex = selectedPairs.findIndex(pair => `${pair.first_id}-${pair.second_id}` === itemId);
          if (pairIndex >= 0) {
            handleBadgeEditClick(selectedPairs[pairIndex], pairIndex);
          }
        } : undefined}
        onItemRemove={(itemId) => {
          const pairIndex = selectedPairs.findIndex(pair => `${pair.first_id}-${pair.second_id}` === itemId);
          if (pairIndex >= 0) {
            const updatedPairs = selectedPairs.filter((_, idx) => idx !== pairIndex);
            setSelectedPairs(updatedPairs);
          }
        }}
        className="mt-2"
      />

      {/* Desktop Badges (unchanged) */}
      <div className={cn(
        "mt-2 hidden md:flex md:flex-wrap md:gap-2",
        badgesMaxHeightClass && "overflow-y-auto pr-1",
        badgesMaxHeightClass
      )}>
        {selectedPairs.map((pair, idx) => (
          <Badge
            key={`${pair.first_id}-${pair.second_id}`}
            className={cn(
              "flex items-center gap-1 pr-1",
              manualAdd && "px-3 py-1 text-sm [&>svg]:size-4",
              badgeColors[idx % badgeColors.length]
            )}
          >
            <span
              className={cn("flex-1 select-none", manualAdd ? "cursor-pointer" : "cursor-default")}
              onClick={() => handleBadgeEditClick(pair, idx)}
            >
              {pair.name_first} – {pair.name_second}
            </span>
            <button
              type="button"
              className="h-4 w-4 ml-1 flex items-center justify-center hover:opacity-80"
              onClick={(e) => removeBadge(idx, e)}
              aria-label="Remove"
            >
              <FaXmark className="h-4 w-4" />
            </button>
          </Badge>
        ))}
      </div>

      {/* Error message */}
      {errors[name]?.message && (
        <span className="text-red-500 text-xs mt-1 block">
          {String(errors[name]?.message)}
        </span>
      )}
    </div>
  );
}
